<template>
  <transition-group name="toast" tag="div" class="toast-container" :class="{ 'is-mobile': isMobile, 'is-dark': isDark }">
    <div 
      v-for="item in items" 
      :key="item.id" 
      class="toast-item" 
      :data-id="item.id"
      :class="[item.type, { 'is-mobile': isMobile, 'is-dark': isDark, 'is-paused': item.isPaused, 'is-swiping': item._swiping, 'is-swipe-closing': item._swipeClosing }]"
      :style="item._swipeStyle || {}"
      @mouseenter="pause(item)"
      @mouseleave="resume(item)"
    >
      <div class="toast-glass-bg"></div>
      <div class="toast-icon">
        <i v-if="item.type === 'success'" class="fa-solid fa-circle-check"></i>
        <i v-else-if="item.type === 'error'" class="fa-solid fa-circle-xmark"></i>
        <i v-else-if="item.type === 'warning'" class="fa-solid fa-circle-exclamation"></i>
        <i v-else class="fa-solid fa-circle-info"></i>
      </div>
      <div class="toast-content" style="white-space: pre-line;">
        <div v-if="item.title" class="toast-title">{{ item.title }}</div>
        <div class="toast-message">
          <component v-if="isVNode(item.message)" :is="renderVNode(item.message)" />
          <template v-else-if="typeof item.message === 'string'">
            {{ item.message }}
          </template>
          <template v-else>
            {{ String(item.message).includes('[object') ? '内容加载中...' : item.message }}
          </template>
        </div>
      </div>
      <button class="toast-close" @click="remove(item.id)">
        <i class="fa-solid fa-xmark"></i>
      </button>
      <div class="toast-progress" :style="{ animationDuration: item.duration + 'ms' }"></div>
    </div>
  </transition-group>
</template>

<script>
import eventBus from '@/utils/eventBus'

export default {
  name: 'CustomToast',
  data() {
    return {
      items: [],
      isMobile: false,
      isDark: false
    }
  },
  mounted() {
    this.checkDarkMode()
    eventBus.on('notify', this.add)
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
    if (typeof MutationObserver !== 'undefined') {
      this._darkObserver = new MutationObserver(() => {
        this.checkDarkMode()
      })
      if (document.body) {
        this._darkObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] })
      }
      if (document.documentElement) {
        this._darkObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
      }
    }
    // 手动绑定非被动触摸事件，以便 preventDefault 阻止页面滚动
    this.$el.addEventListener('touchstart', this._onTouchStart, { passive: false })
    this.$el.addEventListener('touchmove', this._onTouchMove, { passive: false })
    this.$el.addEventListener('touchend', this._onTouchEnd, { passive: false })
  },
  beforeUnmount() {
    eventBus.off('notify', this.add)
    window.removeEventListener('resize', this.checkMobile)
    if (this._darkObserver) {
      this._darkObserver.disconnect()
    }
    this.$el.removeEventListener('touchstart', this._onTouchStart)
    this.$el.removeEventListener('touchmove', this._onTouchMove)
    this.$el.removeEventListener('touchend', this._onTouchEnd)
  },
  methods: {
    checkDarkMode() {
      this.isDark = (document.body && document.body.classList.contains('dark-theme')) || 
                    (document.documentElement && document.documentElement.classList.contains('dark-theme'))
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    isVNode(msg) {
      if (!msg || typeof msg !== 'object') return false
      // Single VNode
      if (msg.__v_isVNode === true || msg.type) return true
      // Array of siblings (Fragment-like)
      if (Array.isArray(msg) && msg.length > 0) {
        return msg.every(item => item && (item.__v_isVNode === true || typeof item === 'string'))
      }
      return false
    },
    renderVNode(vnode) {
      return {
        render() {
          return vnode
        }
      }
    },
    add(payload) {
      const msg = payload.message || ''
      const type = payload.type || 'info'
      
      // 全局防短时间重复提示（1.5秒内完全相同的内容和类型则消抖合并，不重复弹出）
      const now = Date.now()
      const isDuplicate = this.items.some(item => {
        return item.message === msg && item.type === type && (now - item.startTime < 1500)
      })
      
      if (isDuplicate) {
        // 如果重复了，重置现有提示的定时器与进度条，而不新增提示气泡
        const existingItem = this.items.find(item => item.message === msg && item.type === type)
        if (existingItem) {
          clearTimeout(existingItem.timer)
          existingItem.startTime = now
          existingItem.remaining = existingItem.duration
          existingItem.isPaused = false
          existingItem.timer = setTimeout(() => {
            this.remove(existingItem.id)
          }, existingItem.duration)
        }
        return
      }

      const id = Date.now() + Math.random().toString(36).substring(2, 9)
      const duration = payload.duration || 3000
      
      const item = {
        id,
        type,
        title: payload.title,
        message: msg,
        duration,
        remaining: duration,
        startTime: now,
        isPaused: false,
        timer: null,
        onClose: payload.onClose,
        _touchStartY: null,
        _swiping: false,
        _swipeClosing: false,
        _swipeStyle: {}
      }
      
      this.items.unshift(item)
      
      item.timer = setTimeout(() => {
        this.remove(id)
      }, duration)
    },
    pause(item) {
      if (item.isPaused) return
      
      clearTimeout(item.timer)
      item.isPaused = true
      // 计算剩余时间
      item.remaining -= (Date.now() - item.startTime)
    },
    resume(item) {
      if (!item.isPaused) return
      
      item.isPaused = false
      item.startTime = Date.now()
      
      // 重新设置定时器
      item.timer = setTimeout(() => {
        this.remove(item.id)
      }, item.remaining)
    },
    remove(id) {
      const item = this.items.find(i => i.id === id)
      if (item) {
        if (item.timer) clearTimeout(item.timer)
        if (typeof item.onClose === 'function') {
          item.onClose()
        }
      }
      this.items = this.items.filter(item => item.id !== id)
    },
    _findItemByEvent(e) {
      const toastEl = e.target.closest('.toast-item')
      if (!toastEl) return null
      const itemId = toastEl.dataset.id
      if (!itemId) return null
      return this.items.find(i => String(i.id) === String(itemId)) || null
    },
    _onTouchStart(e) {
      if (!this.isMobile) return
      const item = this._findItemByEvent(e)
      if (!item) return
      const touch = e.touches[0]
      item._touchStartY = touch.clientY
      item._swiping = false
      item._swipeStyle = {}
    },
    _onTouchMove(e) {
      if (!this.isMobile) return
      const item = this._findItemByEvent(e)
      if (!item || item._touchStartY == null) return

      const touch = e.touches[0]
      const deltaY = touch.clientY - item._touchStartY
      // 只响应上划（负方向）
      if (deltaY < -10) {
        e.preventDefault()
        item._swiping = true
        const opacity = Math.max(0.3, 1 + deltaY / 200)
        item._swipeStyle = {
          transform: `translateY(${deltaY}px)`,
          opacity: opacity,
          transition: 'none'
        }
      } else if (deltaY > 10) {
        // 下划时回弹
        item._swiping = false
        item._swipeStyle = {
          transform: `translateY(0px)`,
          transition: 'transform 0.3s cubic-bezier(0.23, 1, 0.32, 1)'
        }
      }
    },
    _onTouchEnd(e) {
      if (!this.isMobile) return
      const item = this._findItemByEvent(e)
      if (!item || item._touchStartY == null) return
      const touch = e.changedTouches[0]
      const deltaY = touch.clientY - item._touchStartY
      item._touchStartY = null
      
      // 上划超过 80px 则关闭
      if (deltaY < -80) {
        item._swipeClosing = true
        item._swipeStyle = {
          transform: 'translateY(-100%)',
          opacity: 0,
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 1, 1), opacity 0.3s ease'
        }
        setTimeout(() => {
          this.remove(item.id)
        }, 300)
      } else {
        // 回弹
        item._swiping = false
        item._swipeStyle = {
          transform: 'translateY(0px)',
          opacity: 1,
          transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease'
        }
        setTimeout(() => {
          if (item._swipeStyle && item._swipeStyle.transform === 'translateY(0px)') {
            item._swipeStyle = {}
          }
        }, 400)
      }
    }
  }
}
</script>

<style scoped>
/* Base Reset for component */
.toast-container *, .toast-container *::before, .toast-container *::after {
  box-sizing: border-box;
}

.toast-container {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: var(--z-toast);
  display: flex;
  flex-direction: column;
  align-items: flex-end; /* Align items to the right in desktop */
  gap: 16px;
  pointer-events: none;
}

.toast-item {
  pointer-events: auto;
  width: auto;
  min-width: 320px;
  max-width: 420px;
  padding: 16px 20px;
  position: relative;
  border-radius: 20px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
}

.toast-glass-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(25px) saturate(180%);
  -webkit-backdrop-filter: blur(25px) saturate(180%);
  z-index: -1;
}

/* Glass highlight effect */
.toast-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.2), transparent);
  pointer-events: none;
}

/* Types Accents */
.toast-item.success { border-color: rgba(76, 175, 80, 0.4); box-shadow: 0 12px 40px rgba(76, 175, 80, 0.15); }
.toast-item.error { border-color: rgba(244, 67, 54, 0.4); box-shadow: 0 12px 40px rgba(244, 67, 54, 0.15); }
.toast-item.warning { border-color: rgba(255, 152, 0, 0.4); box-shadow: 0 12px 40px rgba(255, 152, 0, 0.15); }
.toast-item.info { border-color: rgba(64, 158, 255, 0.4); box-shadow: 0 12px 40px rgba(64, 158, 255, 0.15); }

.toast-icon {
  font-size: 22px;
  flex-shrink: 0;
  margin-top: 1px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}

.success .toast-icon { color: #4caf50; }
.error .toast-icon { color: #f44336; }
.warning .toast-icon { color: #ff9800; }
.info .toast-icon { color: #409eff; }

.toast-content {
  flex: 1;
  min-width: 0; /* Critical for text wrapping in flex */
  z-index: 1;
}

.toast-title {
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 6px;
  color: #1d1d1f;
  font-family: '爱点风雅黑', sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.toast-message {
  font-size: 14.5px;
  color: #48484a;
  line-height: 1.55;
  font-weight: 500;
  word-wrap: break-word;
  word-break: break-word; /* Better cross-browser behavior */
}

.toast-close {
  background: rgba(0, 0, 0, 0.05);
  border: none;
  color: #8e8e93;
  cursor: pointer;
  padding: 6px;
  border-radius: 50%;
  transition: all 0.2s;
  font-size: 14px;
  margin-top: -2px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  flex-shrink: 0;
}

.toast-close:hover {
  background: rgba(244, 67, 54, 0.1);
  color: #f44336;
  transform: rotate(90deg);
}

.toast-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 4px;
  background: rgba(0, 0, 0, 0.08);
  width: 100%;
  transform-origin: left;
  animation: progress linear forwards;
}

.toast-item.is-paused .toast-progress {
  animation-play-state: paused;
}

.success .toast-progress { background: #4caf50; }
.error .toast-progress { background: #f44336; }
.warning .toast-progress { background: #ff9800; }
.info .toast-progress { background: #409eff; }

@keyframes progress {
  from { transform: scaleX(1); }
  to { transform: scaleX(0); }
}

/* Mobile adjustments */
.toast-container.is-mobile {
  top: 12px;
  left: 12px !important;
  right: 12px !important;
  width: auto;
  align-items: center;
}

.toast-item.is-mobile {
  min-width: 0;
  width: 100%;
  max-width: 100%;
  padding: 10px 14px; /* Slightly tighter padding */
  border-radius: 16px;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
}

.toast-item.is-swiping {
  transition: none !important;
}

.toast-item.is-swipe-closing {
  pointer-events: none;
}

.toast-item.is-mobile .toast-message {
  word-break: break-word; /* Balanced wrapping */
  white-space: normal;
}

/* Transitions */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.toast-enter-from {
  opacity: 0;
  transform: translateY(-100%);
}

.toast-enter-to {
  opacity: 1;
  transform: translate(0, 0) scale(1);
}

.toast-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.toast-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.toast-move {
  transition: transform 0.4s ease;
}

/* Dark Mode Support within scoped */
.toast-item.is-dark {
  border-color: rgba(255, 255, 255, 0.12);
  box-shadow: 0 16px 45px rgba(0, 0, 0, 0.6);
}

.toast-item.is-dark::before {
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.08), transparent);
}

.toast-item.is-dark .toast-glass-bg {
  background: rgba(28, 28, 32, 0.82);
  backdrop-filter: blur(30px) saturate(190%);
  -webkit-backdrop-filter: blur(30px) saturate(190%);
}

.toast-item.is-dark .toast-title {
  color: #f5f5f7;
}

.toast-item.is-dark .toast-message {
  color: #d1d1d6;
}

.toast-item.is-dark .toast-close {
  background: rgba(255, 255, 255, 0.08);
  color: #a1a1aa;
}

.toast-item.is-dark .toast-close:hover {
  background: rgba(255, 69, 58, 0.25);
  color: #ff5252;
}

.toast-item.is-dark .toast-progress {
  background: rgba(255, 255, 255, 0.1);
}

/* Specific Accent Glows for Dark Mode */
.toast-item.is-dark.success {
  border-color: rgba(76, 175, 80, 0.35);
  box-shadow: 0 16px 45px rgba(0, 0, 0, 0.5), 0 0 20px rgba(76, 175, 80, 0.15);
}
.toast-item.is-dark.error {
  border-color: rgba(244, 67, 54, 0.35);
  box-shadow: 0 16px 45px rgba(0, 0, 0, 0.5), 0 0 20px rgba(244, 67, 54, 0.15);
}
.toast-item.is-dark.warning {
  border-color: rgba(255, 152, 0, 0.35);
  box-shadow: 0 16px 45px rgba(0, 0, 0, 0.5), 0 0 20px rgba(255, 152, 0, 0.15);
}
.toast-item.is-dark.info {
  border-color: rgba(64, 158, 255, 0.35);
  box-shadow: 0 16px 45px rgba(0, 0, 0, 0.5), 0 0 20px rgba(64, 158, 255, 0.15);
}
</style>

<style>
/* 全局深色模式保障（当 body 或 html 具有 dark-theme 时强行覆盖，确保绝对兼容） */
body.dark-theme .toast-item,
html.dark-theme .toast-item {
  border-color: rgba(255, 255, 255, 0.12) !important;
  box-shadow: 0 16px 45px rgba(0, 0, 0, 0.6) !important;
}

body.dark-theme .toast-item::before,
html.dark-theme .toast-item::before {
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.08), transparent) !important;
}

body.dark-theme .toast-glass-bg,
html.dark-theme .toast-glass-bg {
  background: rgba(28, 28, 32, 0.85) !important;
  backdrop-filter: blur(30px) saturate(190%) !important;
  -webkit-backdrop-filter: blur(30px) saturate(190%) !important;
}

body.dark-theme .toast-title,
html.dark-theme .toast-title {
  color: #f5f5f7 !important;
}

body.dark-theme .toast-message,
html.dark-theme .toast-message {
  color: #d1d1d6 !important;
}

body.dark-theme .toast-message div,
body.dark-theme .toast-message p,
body.dark-theme .toast-message span:not([style*="color"]) {
  color: inherit;
}

body.dark-theme .toast-close,
html.dark-theme .toast-close {
  background: rgba(255, 255, 255, 0.08) !important;
  color: #a1a1aa !important;
}

body.dark-theme .toast-close:hover,
html.dark-theme .toast-close:hover {
  background: rgba(255, 69, 58, 0.25) !important;
  color: #ff5252 !important;
}

body.dark-theme .toast-item.success,
html.dark-theme .toast-item.success {
  border-color: rgba(76, 175, 80, 0.35) !important;
  box-shadow: 0 16px 45px rgba(0, 0, 0, 0.5), 0 0 20px rgba(76, 175, 80, 0.15) !important;
}
body.dark-theme .toast-item.error,
html.dark-theme .toast-item.error {
  border-color: rgba(244, 67, 54, 0.35) !important;
  box-shadow: 0 16px 45px rgba(0, 0, 0, 0.5), 0 0 20px rgba(244, 67, 54, 0.15) !important;
}
body.dark-theme .toast-item.warning,
html.dark-theme .toast-item.warning {
  border-color: rgba(255, 152, 0, 0.35) !important;
  box-shadow: 0 16px 45px rgba(0, 0, 0, 0.5), 0 0 20px rgba(255, 152, 0, 0.15) !important;
}
body.dark-theme .toast-item.info,
html.dark-theme .toast-item.info {
  border-color: rgba(64, 158, 255, 0.35) !important;
  box-shadow: 0 16px 45px rgba(0, 0, 0, 0.5), 0 0 20px rgba(64, 158, 255, 0.15) !important;
}
</style>

